import { trackEvent } from './tracking';

import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Types
import { OrganizationRoleType } from 'Shared/types/Member.type';
import { PlanNames, PlanAddOnNames, PlanIntervalBasis } from 'Shared/types/Plan.types';
import { Feature } from 'Shared/types/Organization.type';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useOrganization } from 'Shared/utils/hooks/use-organization';

export const useSubscriptionTracking = () => {
  const { user: currentUser } = useCurrentUser();
  const {
    organization: { billingState, seatsToBillFor, trialAlreadyBeenExtended },
  } = useOrganization();
  const { appId } = useApp();

  const isAdmin = currentUser.organizationRole === OrganizationRoleType.admin;

  const track = ({ eventName, properties = {}, options = {} }) => {
    const propertiesToSend = {
      product_feature: 'Subscriptions',
      user: currentUser.id,
      app_id: appId,
      is_user_admin: isAdmin,
      current_billing_state: billingState,
      seats_to_bill_for: seatsToBillFor,
      trial_already_been_extended: trialAlreadyBeenExtended,
      ...properties,
    };

    trackEvent(eventName, propertiesToSend, options);
  };

  const trackViewedCheckoutPage = () => {
    track({ eventName: 'Viewed Checkout Page' });
  };

  const trackViewedTrialExpiredModal = () => {
    track({ eventName: 'Viewed Trial Expired Modal', options: { integrations: { Calixa: false } } });
  };

  const trackViewedCostBreakdown = () => {
    track({ eventName: 'Viewed Cost Breakdown' });
  };

  const trackViewPlansButtonClick = ({
    from,
    selected_plan,
  }: {
    from:
      | 'billing_timer_popover'
      | 'trial_expired_modal'
      | 'user_limit_exceeded_banner'
      | 'billing_page'
      | 'goals_feature_blocker'
      | 'reviews_feature_blocker';
    selected_plan?: PlanNames;
  }) => {
    track({ eventName: 'Clicked to view plans', properties: { from, selected_plan } });
  };

  const trackSubscribedToPlan = ({
    selected_plan,
    selected_interval,
    selected_addons,
    hasCoupon,
  }: {
    selected_plan: PlanNames;
    selected_interval?: PlanIntervalBasis;
    selected_addons?: PlanAddOnNames[];
    hasCoupon?: boolean;
  }) => {
    track({
      eventName: 'Subscribed to plan',
      properties: { selected_plan, selected_interval, selected_addons, has_coupon: hasCoupon },
    });
  };

  const trackClickedCancelSubscription = () => {
    track({ eventName: 'Clicked cancel subscription' });
  };

  const trackedClickedExtendTrialButton = () => {
    track({ eventName: 'Clicked extend trial button' });
  };

  const trackClickedManageUsersFromUsersExceededBanner = () => {
    track({ eventName: 'Clicked Manage Users from exceeded user limit banner' });
  };

  const trackViewedFeatureGate = ({ feature }: { feature: Feature }) => {
    track({ eventName: 'Viewed Feature Gate', properties: { feature } });
  };

  const trackClickedDemoOnSuccessModal = () => {
    track({ eventName: 'Clicked to Book a Demo' });
  };

  return {
    trackViewPlansButtonClick,
    trackSubscribedToPlan,
    trackViewedCostBreakdown,
    trackViewedTrialExpiredModal,
    trackViewedCheckoutPage,
    trackClickedCancelSubscription,
    trackedClickedExtendTrialButton,
    trackClickedManageUsersFromUsersExceededBanner,
    trackViewedFeatureGate,
    trackClickedDemoOnSuccessModal,
  };
};
